import React from "react";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Chip,
  CssBaseline,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import MarkEmailUnreadRoundedIcon from "@mui/icons-material/MarkEmailUnreadRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";

import Logo from "../../assets/Logo.png";
import { BANK_NAME, BANK_SHORT_NAME } from "../../constants/bank";
import "./home.css";
import { signOut } from "../../Api/index";

function getStoredUser() {
  try {
    return JSON.parse(secureLocalStorage.getItem("profile"));
  } catch {
    return null;
  }
}

export default function Header() {
  const user = React.useMemo(() => getStoredUser(), []);
  const currentUser = user?.results || {};

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [anchorElServices, setAnchorElServices] = React.useState(null);
  const [anchorElNotifications, setAnchorElNotifications] =
    React.useState(null);

  const servicesOpen = Boolean(anchorElServices);
  const notificationsOpen = Boolean(anchorElNotifications);

  const handleOpenServices = (event) => {
    setAnchorElServices(event.currentTarget);
  };

  const handleCloseServices = () => {
    setAnchorElServices(null);
  };

  const handleOpenNotifications = (event) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorElNotifications(null);
  };

  const goTo = (path) => {
    navigate(path);
    handleCloseServices();
  };

  const logout = () => {
    const loggingOutId = toast.loading("Please wait we are logging you out...");

    // Ends the shared login, so the other apps are logged out too
    signOut().catch(() => {});
    dispatch({ type: "LOGOUT" });

    setTimeout(() => {
      toast.remove(loggingOutId);
      toast.success("You are out, see you again!");
      navigate("/admin/login");
    }, 1200);

    handleCloseServices();
  };

  return (
    <>
      <CssBaseline />

      <AppBar
        elevation={0}
        position="fixed"
        sx={{
          width: "100%",
          background: "rgba(15, 23, 42, .92)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(255,255,255,.10)",
          boxShadow: "0 14px 35px rgba(15, 23, 42, .18)",
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 10,
        }}
      >
        <Box
          sx={{
            height: 62,
            px: { xs: 1.5, md: 2.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Brand */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                background: "rgba(255,255,255,.95)",
                border: "1px solid rgba(255,255,255,.35)",
                boxShadow: "0 10px 24px rgba(0,0,0,.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={Logo}
                alt={`${BANK_NAME} logo`}
                sx={{
                  width: 38,
                  height: 38,
                  objectFit: "contain",
                }}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: "#fff",
                  fontWeight: 950,
                  fontSize: { xs: 14, sm: 17, md: 20 },
                  lineHeight: 1.1,
                  letterSpacing: 0.2,
                }}
              >
                {BANK_NAME}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.5 }}
              >
                <Chip
                  size="small"
                  icon={<AdminPanelSettingsRoundedIcon />}
                  label="Admin Console"
                  sx={{
                    height: 22,
                    borderRadius: 999,
                    color: "#bfdbfe",
                    background: alpha("#1677ff", 0.14),
                    border: "1px solid rgba(147,197,253,.22)",
                    fontWeight: 800,
                    "& .MuiChip-icon": {
                      color: "#93c5fd",
                      fontSize: 15,
                    },
                  }}
                />

                {currentUser?.branch && (
                  <Typography
                    noWrap
                    sx={{
                      color: "#94a3b8",
                      fontSize: 11,
                      maxWidth: { xs: 80, sm: 160 },
                      textTransform: "uppercase",
                    }}
                  >
                    {currentUser.branch}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>

          {/* Middle Services */}
          {/* Every admin needs Settings (password) and Logout, on any screen */}
          {user && (
            <Button
              disableElevation
              id="services-button"
              aria-controls={servicesOpen ? "services-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={servicesOpen ? "true" : undefined}
              onClick={handleOpenServices}
              endIcon={<KeyboardArrowDownRoundedIcon />}
              sx={{
                display: "inline-flex",
                flexShrink: 0,
                height: 38,
                px: { xs: 1.5, md: 2 },
                borderRadius: 999,
                color: "#e2e8f0",
                fontWeight: 900,
                textTransform: "none",
                background: servicesOpen
                  ? "rgba(255,255,255,.14)"
                  : "rgba(255,255,255,.08)",
                border: "1px solid rgba(255,255,255,.12)",
                "&:hover": {
                  background: "rgba(255,255,255,.16)",
                },
              }}
            >
              Services
            </Button>
          )}

          {/* Right side */}
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Tooltip title="Toggle notifications panel">
              <IconButton
                onClick={handleOpenNotifications}
                sx={{
                  width: 40,
                  height: 40,
                  color: "#e2e8f0",
                  background: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.10)",
                  "&:hover": {
                    background: "rgba(255,255,255,.16)",
                  },
                }}
              >
                <Badge color="secondary" badgeContent={0} max={100}>
                  <NotificationsRoundedIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                textAlign: "right",
                pr: 0.5,
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 950,
                  fontSize: 16,
                  lineHeight: 1,
                }}
              >
                {BANK_SHORT_NAME}.
                <Box
                  component="span"
                  sx={{
                    color: "#93c5fd",
                    fontStyle: "italic",
                    fontWeight: 700,
                    ml: 0.2,
                  }}
                >
                  users
                </Box>
              </Typography>

              <Typography
                noWrap
                sx={{
                  color: "#94a3b8",
                  fontSize: 11,
                  maxWidth: 160,
                }}
              >
                {currentUser?.firstName
                  ? `${currentUser.firstName} ${currentUser.lastName || ""}`
                  : "Signed in"}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </AppBar>

      {/* Services Menu */}
      <Menu
        id="services-menu"
        anchorEl={anchorElServices}
        open={servicesOpen}
        onClose={handleCloseServices}
        MenuListProps={{
          "aria-labelledby": "services-button",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.2,
            minWidth: 220,
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            boxShadow: "0 22px 55px rgba(15, 23, 42, .18)",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.4,
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 13 }}>
            Services
          </Typography>
          <Typography sx={{ color: "#94a3b8", fontSize: 11 }}>
            Admin tools
          </Typography>
        </Box>

        <MenuItem
          onClick={() => goTo("/admin/settings")}
          sx={{ gap: 1.2, py: 1.2 }}
        >
          <SettingsRoundedIcon fontSize="small" sx={{ color: "#1d4ed8" }} />
          Settings
        </MenuItem>

        <MenuItem onClick={() => goTo("/admin")} sx={{ gap: 1.2, py: 1.2 }}>
          <PeopleAltRoundedIcon fontSize="small" sx={{ color: "#15803d" }} />
          Manage Users
        </MenuItem>

        <MenuItem
          onClick={() => goTo("/admin/security")}
          sx={{ gap: 1.2, py: 1.2 }}
        >
          <SecurityRoundedIcon fontSize="small" sx={{ color: "#7c3aed" }} />
          Sign-in Security
        </MenuItem>

        <Divider />

        <MenuItem onClick={logout} sx={{ gap: 1.2, py: 1.2, color: "#be123c" }}>
          <LogoutRoundedIcon fontSize="small" />
          Logout
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        id="notifications-menu"
        anchorEl={anchorElNotifications}
        open={notificationsOpen}
        onClose={handleCloseNotifications}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.2,
            width: 320,
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            boxShadow: "0 22px 55px rgba(15, 23, 42, .18)",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <MarkEmailUnreadRoundedIcon sx={{ color: "#93c5fd" }} />
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 14 }}>
                Notifications
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: 11 }}>
                Latest system messages
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ px: 2, py: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              background: "#f8fafc",
              border: "1px dashed #cbd5e1",
              textAlign: "center",
            }}
          >
            <NotificationsRoundedIcon sx={{ color: "#94a3b8", mb: 0.6 }} />

            <Typography
              sx={{ fontWeight: 900, color: "#334155", fontSize: 13 }}
            >
              No messages available
            </Typography>

            <Typography sx={{ color: "#64748b", fontSize: 12, mt: 0.5 }}>
              New alerts will appear here.
            </Typography>
          </Box>
        </Box>
      </Menu>
    </>
  );
}
